import React from "react"
import { Flex, Text } from "@chakra-ui/react"
import { EventCounts } from "sharedTypes"
import Icon from "components/elements/Icon"
import Popover from "components/dialogs/Popover"
import { asNumber, asPercent } from "utilities/strings"
import { withSectionBoundary } from "components/ErrorBoundary"

interface Props {
  counts: EventCounts
  showSummary?: boolean
}

type Indicator = {
  icon?: string
  iconSize?: number
  dotColor?: string
  color?: string
  count: number
  popoverLabel: string
  showPercentage?: boolean
}

const StatIndicator = ({
  icon,
  iconSize,
  dotColor,
  color,
  count,
  popoverLabel,
  showPercentage,
  total,
  size,
}: Indicator & { total: number; size: "main" | "dot" }) => {
  if (count === 0) return null

  const percentage = total > 0 ? asPercent(count / total) : "0%"

  const defaultIconSize = size === "main" ? 5 : 3
  const iconElement = icon
    ? <Icon icon={icon} size={iconSize || defaultIconSize} color={color} />
    : <Icon icon="dot" size={3} color={dotColor} />

  const trigger = (
    <Flex align="center" gap={1.5}>
      {iconElement}
      <Text
        fontSize={size === "main" ? "lg" : "md"}
        fontWeight={size === "main" ? "semibold" : "medium"}
      >
        {asNumber(count)}
      </Text>
    </Flex>
  )

  const body = (
    <Flex px={5} py={3} align="center" justify="space-between" gap={8}>
      <Flex align="center" gap={1.5}>
        {icon
          ? <Icon icon={icon} size={4} color={color} />
          : <Icon icon="dot" size={3} color={dotColor} />}
        <Text fontSize="md">
          {popoverLabel}
        </Text>
      </Flex>
      <Text fontSize="md" fontWeight="medium">
        {showPercentage ? percentage : asNumber(count)}
      </Text>
    </Flex>
  )

  return (
    <Popover
      triggerEvent="hover"
      placement="bottom"
      arrow
      contentWidth="auto"
      trigger={trigger}
      body={body}
    />
  )
}

const GuestStatBar = ({ counts, showSummary = false }: Props) => {
  const mainStats: (Indicator & { size: "main" })[] = [
    {
      icon: "status-all",
      iconSize: 7,
      color: "blue.500",
      count: counts.guests,
      popoverLabel: "All Guests",
      size: "main",
    },
    {
      icon: "checkin-filled",
      color: "green.500",
      count: counts.arrivedGuests,
      popoverLabel: "Checked In",
      showPercentage: true,
      size: "main",
    },
  ]

  const confirmationDots: (Indicator & { size: "dot" })[] = [
    {
      dotColor: "blue.300",
      count: counts.confirmedGuests,
      popoverLabel: "Confirmed",
      showPercentage: true,
      size: "dot",
    },
    {
      dotColor: "gray.400",
      count: counts.unconfirmedGuests,
      popoverLabel: "Unconfirmed",
      showPercentage: true,
      size: "dot",
    },
    {
      dotColor: "yellow.300",
      count: counts.waitlistedGuests,
      popoverLabel: "Waitlisted",
      showPercentage: true,
      size: "dot",
    },
    {
      dotColor: "red.300",
      count: counts.declinedGuests,
      popoverLabel: "Declined",
      showPercentage: true,
      size: "dot",
    },
  ]

  return (
    <Flex align="center" gap={8}>
      <Flex align="center" gap={6}>
        {mainStats.map(({ size, ...stat }) => (
          <StatIndicator
            key={stat.popoverLabel}
            {...stat}
            size={size}
            total={counts.guests}
          />
        ))}
      </Flex>
      {showSummary && (
        <Flex align="center" gap={4}>
          {confirmationDots.map(({ size, ...stat }) => (
            <StatIndicator
              key={stat.popoverLabel}
              {...stat}
              size={size}
              total={counts.guests}
            />
          ))}
        </Flex>
      )}
    </Flex>
  )
}

// Renders event counts straight from the API (DIO-960, DIO-961).
export default withSectionBoundary(GuestStatBar, { name: "guest-stats", message: "Stats unavailable." })
